'use client';

import { useFocusGuards } from '@radix-ui/react-focus-guards';
import { FocusScope } from '@radix-ui/react-focus-scope';
import { ComponentPropsWithoutRef, ComponentProps, Fragment } from 'react';
import { cn } from '@/shared/lib/cn';
import { WithDataAttributes } from '@/shared/model/WithDataAttributes';
import { Layout, MediaStyles, mediaToClassName } from '@/shared/ui/layout';
import { useModalContext } from './modal-context';
import './modal.scss';
import { useModalsStore } from './store';

const FocusGuardsEffect = () => {
    useFocusGuards();
    return null;
};

type Props = ComponentPropsWithoutRef<'div'> & {
    containerProps?: WithDataAttributes<ComponentProps<'div'>>;
    scrollerProps?: WithDataAttributes<ComponentProps<typeof Layout>>;
    media?: MediaStyles;
    focusScope?: boolean;
};
export const ModalWrapper = ({
    children,
    className,
    containerProps,
    scrollerProps,
    media,
    focusScope = true,
    ...props
}: Props) => {
    const { modalId, isClosing } = useModalContext();
    const isServiceModal = useModalsStore((s) => modalId in s.data);
    const Wrapper = focusScope ? FocusScope : Fragment;
    const wrapperProps = focusScope ? { asChild: true as const, loop: isServiceModal } : {};

    return (
        <Wrapper {...wrapperProps}>
            <div {...containerProps} className={cn('modal__container', containerProps?.className)}>
                {isServiceModal && focusScope && <FocusGuardsEffect />}
                <div
                    id={`modal-${modalId}-wrapper`}
                    className={cn(
                        'modal__wrapper',
                        'modal__wrapper--interactive',
                        isClosing && 'modal__wrapper--closing',
                        media && mediaToClassName(media),
                        className,
                    )}
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                    role={focusScope ? 'dialog' : undefined}
                    aria-modal={focusScope ? true : undefined}
                    {...props}
                >
                    <Layout {...scrollerProps} className={cn('modal__scroller', scrollerProps?.className)}>
                        {children}
                    </Layout>
                </div>
            </div>
        </Wrapper>
    );
};
